/**
 * Below 640 px a desktop page offers the phone view (live brief, section 3): a slim bar docked at
 * the foot of the screen with one action, Open phone view, that carries the page across (a
 * session, a mission, demo data), and a close that keeps the console. It replaces the old "This
 * screen is narrow" offer, which could be dismissed for good and then never helped again.
 *
 * The bar takes its own strip: while it shows, the app root keeps padding of the bar's height, so
 * the rail and the page end above it and nothing (the rail's last control, a page's last rows or
 * pagination) sits under it. A bulk selection bar outranks it: while one is open the hand-off
 * steps aside, so the two never stack.
 *
 * "Stay on this page" keeps this tab on the console until it closes (the same session flag the
 * phone view's "Open the full console" link sets), so nobody who chose the console is nagged.
 * Rendered inside the auth gate, so never over the splash or the sign-in form.
 */
import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { RefObject } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { mobileUrl } from '@orgus/protocol';
import { Smartphone, X } from '@orgus/ui/icons';
import { Button, IconButton } from '@orgus/ui';
import { useMediaQuery } from '../../lib/useMediaQuery.ts';

/** Narrower than the console's smallest layout: the `sm` breakpoint, 640 px (40em). */
export const HANDOFF_QUERY = '(max-width: 39.99em)';

/** This tab chose the console: shared with the phone view's "Open the full console" link. */
export const KEEP_CONSOLE_KEY = 'orgus.console';

/** The phone view's address for a console address, keeping demo data on. */
export function phoneViewHref(pathname: string, search: string): string {
  const to = mobileUrl(`${pathname}${search}`);
  const mock = new URLSearchParams(search).get('mock');
  if (!mock || to.includes('mock=')) return to;
  return `${to}${to.includes('?') ? '&' : '?'}mock=${encodeURIComponent(mock)}`;
}

/** Whether to hand off: a narrow screen, on a console page, in a tab that has not chosen the console. */
export function shouldHandOff(narrow: boolean, pathname: string, search: string, kept: string | null): boolean {
  if (!narrow || kept === '1') return false;
  if (pathname === '/m' || pathname.startsWith('/m/')) return false;
  return new URLSearchParams(search).get('console') !== '1';
}

function readKept(): string | null {
  try {
    return sessionStorage.getItem(KEEP_CONSOLE_KEY);
  } catch {
    return null;
  }
}

/** Set on the document element by @orgus/ui's BulkBar while a selection bar is open. */
const BULK_OPEN_ATTR = 'data-bulk-open';

function subscribeBulk(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => {};
  const mo = new MutationObserver(onChange);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: [BULK_OPEN_ATTR] });
  return () => mo.disconnect();
}

/** A bulk selection bar is open somewhere on the page. */
function useBulkOpen(): boolean {
  return useSyncExternalStore(
    subscribeBulk,
    () => typeof document !== 'undefined' && document.documentElement.hasAttribute(BULK_OPEN_ATTR),
    () => false,
  );
}

/** Keeps the app root's foot clear for the docked bar, its height as padding, while it shows. */
function useReservedStrip(ref: RefObject<HTMLElement | null>, on: boolean) {
  useLayoutEffect(() => {
    const bar = ref.current;
    const root = bar?.parentElement;
    if (!on || !bar || !root) return;
    // The page scrolls inside #main, not the root: reserve the strip there too, so the last row clears it.
    const main = document.getElementById('main');
    const before = root.style.paddingBottom;
    const beforeMain = main?.style.paddingBottom ?? '';
    const reserve = () => {
      root.style.paddingBottom = `${bar.offsetHeight}px`;
      if (main) main.style.paddingBottom = `${bar.offsetHeight}px`;
    };
    reserve();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserve);
    ro?.observe(bar);
    return () => {
      ro?.disconnect();
      root.style.paddingBottom = before;
      if (main) main.style.paddingBottom = beforeMain;
    };
  }, [ref, on]);
}

export function PhoneHandOff() {
  const narrow = useMediaQuery(HANDOFF_QUERY);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [kept, setKept] = useState(readKept);
  const bulk = useBulkOpen();
  const ref = useRef<HTMLElement>(null);
  const show = shouldHandOff(narrow, pathname, search, kept) && !bulk;
  useReservedStrip(ref, show);
  if (!show) return null;
  const stay = () => {
    setKept('1');
    try {
      sessionStorage.setItem(KEEP_CONSOLE_KEY, '1');
    } catch {
      // Storage blocked: the console stays for this page only.
    }
  };
  // A slim strip, sized to what it is (a hint, not a dialog): one line, one action, a close.
  return (
    <section
      ref={ref}
      aria-label="Phone view"
      data-handoff=""
      className="fixed inset-x-0 bottom-0 z-(--z-overlay) flex items-center gap-3 border-t border-border bg-raised py-2 pl-4 pr-2"
      style={{ paddingBottom: 'calc(var(--spacing) * 2 + env(safe-area-inset-bottom, 0))' }}
    >
      <Smartphone className="size-4 shrink-0 text-fg-subtle" aria-hidden />
      <p className="min-w-0 flex-1 truncate text-ui text-fg-muted">Easier in the phone view</p>
      <Button variant="primary" size="sm" onClick={() => navigate(phoneViewHref(pathname, search))}>
        Open phone view
      </Button>
      <IconButton size="sm" label="Stay on this page" icon={<X />} onClick={stay} />
    </section>
  );
}
